import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Package2, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { LOGO_URL } from "@/lib/constants";
import { parseKeyValueMap } from "@/lib/pageContent";
import { pickLanguage } from "@/lib/publicLanguage";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { usePublicSettings } from "@/hooks/usePublicSiteData";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { language, setLanguage, languageOptions } = useSiteLanguage();
  const { data: settings } = usePublicSettings();
  const navLabels = parseKeyValueMap(settings?.navbar_labels || "");
  const fallbackLabels = {
    home: pickLanguage(language, {
      en: navLabels.home || "Home",
      "zh-CN": "首页",
      bn: "হোম",
    }),
    products: pickLanguage(language, {
      en: navLabels.products || "Products",
      "zh-CN": "产品",
      bn: "পণ্যসমূহ",
    }),
    company: pickLanguage(language, {
      en: navLabels.company || "Company",
      "zh-CN": "公司介绍",
      bn: "কোম্পানি",
    }),
    trust: pickLanguage(language, {
      en: navLabels.trust || "Trust Center",
      "zh-CN": "信任中心",
      bn: "ট্রাস্ট সেন্টার",
    }),
    track: pickLanguage(language, {
      en: navLabels.track || "Track Order",
      "zh-CN": "订单追踪",
      bn: "অর্ডার ট্র্যাক",
    }),
    cta: pickLanguage(language, {
      en: navLabels.cta || settings?.hero_cta_label || "Inquire Now",
      "zh-CN": "立即咨询",
      bn: "এখনই জিজ্ঞাসা করুন",
    }),
    languageLabel: pickLanguage(language, {
      en: "Language",
      "zh-CN": "语言",
      bn: "ভাষা",
    }),
    openMenu: pickLanguage(language, {
      en: "Open menu",
      "zh-CN": "打开菜单",
      bn: "মেনু খুলুন",
    }),
    closeMenu: pickLanguage(language, {
      en: "Close menu",
      "zh-CN": "关闭菜单",
      bn: "মেনু বন্ধ করুন",
    }),
    mobileNavTitle: pickLanguage(language, {
      en: "R.S. International — Mobile Navigation",
      "zh-CN": "R.S. International — 移动导航",
      bn: "R.S. International — মোবাইল নেভিগেশন",
    }),
  };
  const navItems = [
    { to: "/", label: fallbackLabels.home },
    { to: "/products", label: fallbackLabels.products },
    { to: "/about", label: fallbackLabels.company },
    { to: "/trust-center", label: fallbackLabels.trust },
    { to: "/track", label: fallbackLabels.track },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHome ? "bg-white/70" : "bg-white/85"
      } backdrop-blur-xl border-b border-gray-200/60`}
      data-testid="public-navbar"
    >
      <div className="container-rsi flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <div className="h-11 w-11 rounded-xl overflow-hidden bg-white border border-gray-200 shadow-sm p-1">
            <img src={LOGO_URL} alt="R.S. International" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[15px] font-extrabold tracking-tight text-[var(--rsi-navy)]">
              {settings?.company_name || "R.S. INTERNATIONAL"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">
              {settings?.tagline || "Global Trade Partner"}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[var(--rsi-purple)] bg-[var(--rsi-purple-tint)]"
                    : "text-gray-700 hover:text-[var(--rsi-purple)] hover:bg-[var(--rsi-purple-tint)]/60"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden lg:flex items-center gap-1 rounded-full border border-gray-200 bg-white/80 p-1 shadow-sm">
            {languageOptions.map((option) => (
              <button
                key={option.code}
                type="button"
                onClick={() => setLanguage(option.code)}
                className={`site-lang-toggle ${language === option.code ? "site-lang-toggle--active" : ""}`}
                data-testid={`language-toggle-${option.code.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {option.shortLabel}
              </button>
            ))}
          </div>
          <Link
            to="/contact"
            data-testid="nav-cta-inquire"
            className="hidden md:inline-flex btn-primary"
          >
            {fallbackLabels.cta}
            <ArrowUpRight className="h-4 w-4" />
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden h-11 w-11 rounded-full bg-white border border-gray-200 grid place-items-center"
                aria-label={fallbackLabels.openMenu}
                data-testid="mobile-menu-trigger"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] sm:w-[420px] p-0 bg-white">
              <VisuallyHidden>
                <SheetTitle>{fallbackLabels.mobileNavTitle}</SheetTitle>
              </VisuallyHidden>
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl overflow-hidden bg-white border border-gray-200 p-1">
                    <img src={LOGO_URL} alt="" className="h-full w-full object-contain" />
                  </div>
                  <span className="font-display font-extrabold text-[var(--rsi-navy)]">
                    {settings?.company_name || "R.S. INTERNATIONAL"}
                  </span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="h-9 w-9 rounded-full grid place-items-center bg-gray-100"
                  aria-label={fallbackLabels.closeMenu}
                  data-testid="mobile-menu-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-4">
                <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 p-2">
                  <div className="mb-2 px-2 text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">
                    {fallbackLabels.languageLabel}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {languageOptions.map((option) => (
                      <button
                        key={option.code}
                        type="button"
                        onClick={() => setLanguage(option.code)}
                        className={`site-lang-toggle mobile ${language === option.code ? "site-lang-toggle--active" : ""}`}
                      >
                        {option.nativeLabel}
                      </button>
                    ))}
                  </div>
                </div>
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium ${
                        isActive
                          ? "text-[var(--rsi-purple)] bg-[var(--rsi-purple-tint)]"
                          : "text-gray-800 hover:bg-gray-50"
                      }`
                    }
                  >
                    {item.label}
                    <Package2 className="h-4 w-4 opacity-40" />
                  </NavLink>
                ))}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    className="btn-primary w-full"
                    data-testid="mobile-cta-inquire"
                  >
                    {fallbackLabels.cta}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
